import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Sparkles, Wand2, Loader2, FileText, Play, Pencil, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { generateFrsEnhanced, getFrsContent, generateAppFromFrs, getJson, proposeScope, prepareDraft } from '@/api/generateapp'
import AddSourceModal, {
  SOURCE_TYPES, type AttachedSource, type SourceKind,
} from '@/components/sources/AddSourceModal'
import AddSourceButton from '@/components/sources/AddSourceButton'
import ScopeConfirmation from '@/components/agent/ScopeConfirmation'
import type { ScopeProposal, ScopeDecisions } from '@/types'
import { saveDraft } from '@/api/workbench'
import ReferenceProjectPicker from '@/components/common/ReferenceProjectPicker'
import StartBlankForm from '@/components/common/StartBlankForm'
import AttachmentChips from '@/components/common/AttachmentChips'
import { useAttachments } from '@/hooks/useAttachments'
import { ATTACHMENT_ACCEPT, toWire } from '@/lib/attachments'
import { getSettings } from '@/api/settings'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import { useProjectStore } from '@/store/projectStore'
import AgentStreamPanel from '@/components/common/AgentStreamPanel'

const SURPRISE_PROMPTS = [
  'A pet adoption platform where shelters can list animals and users can apply to adopt with a matching algorithm',
  'An employee leave management system with approval workflows, calendar view, and balance tracking',
  'A recipe sharing social network where users rate recipes, create meal plans, and generate shopping lists',
  'A project time-tracking application with team dashboards, invoicing, and client reporting',
  'A school library management system with book cataloging, student borrowing, and overdue notifications',
  'A fitness studio booking platform with class schedules, trainer profiles, and membership management',
]

export default function DescribeAppPage() {
  const navigate = useNavigate()
  const { fetchProjects, selectProject } = useProjectStore()

  const [description, setDescription] = useState('')
  const att = useAttachments()
  // Sources attached from the "+ Add a source" menu (a database connection, a
  // git/zip reference source, an IOFlow connector) — same system as the Ask-AI
  // composer. Each contributes a machine-readable line that LEADS the outgoing
  // description, so the build resolves the exact source (and its registered
  // connection) instead of inferring one from the prose. The user can also
  // refer to any attachment by its shown name inside the prompt.
  const [sources, setSources] = useState<AttachedSource[]>([])
  const [sourceModalKind, setSourceModalKind] = useState<SourceKind | null>(null)
  const [preparingSource, setPreparingSource] = useState(false)
  const [rightPanelOpen, setRightPanelOpen] = useState(false)
  const [suggestEnhancements, setSuggestEnhancements] = useState(false)
  const [enhanceScope, setEnhanceScope] = useState(false)
  const [selfReview, setSelfReview] = useState(false)
  const [reviewCount, setReviewCount] = useState(1)
  const [generatingFrs, setGeneratingFrs] = useState(false)
  const [proposingScope, setProposingScope] = useState(false)
  const [scopeProposal, setScopeProposal] = useState<ScopeProposal | null>(null)
  const [generatingApp, setGeneratingApp] = useState(false)
  const [progress, setProgress] = useState(0)
  const [generationMode, setGenerationMode] = useState<string>('llm')
  const [effortConfig, setEffortConfig] = useState<Record<string, string>>({})
  const [agentPanelOpen, setAgentPanelOpen] = useState(false)
  const [generatedFrs, setGeneratedFrs] = useState<string | null>(null)
  const [checkingDraft, setCheckingDraft] = useState(true)
  const [selectedRefs, setSelectedRefs] = useState<Array<{ folder_name: string; owner_user_id: string }>>([])

  // Load generation mode and thinking effort from settings
  useEffect(() => {
    getSettings().then((s) => {
      setGenerationMode(s.generation_mode || 'llm')
      if (s.thinking_effort_config) setEffortConfig(s.thinking_effort_config)
    }).catch(() => { /* use default */ })
  }, [])

  // On mount, check if Draft Application already has an FRS
  useEffect(() => {
    setCheckingDraft(true)
    getFrsContent('Draft Application')
      .then((data) => {
        if (data.content) {
          setGeneratedFrs(data.content)
        }
      })
      .catch(() => { /* No existing draft - that's fine */ })
      .finally(() => setCheckingDraft(false))
  }, [])

  // After FRS is set, refresh sidebar and select Draft Application
  const onFrsGenerated = useCallback((content: string) => {
    setGeneratedFrs(content)
    setRightPanelOpen(false)
    setAgentPanelOpen(false)
    // Refresh the sidebar project list so Draft Application appears
    fetchProjects().then(() => {
      selectProject('Draft Application')
    })
  }, [fetchProjects, selectProject])

  // Track which agent task is active (frs vs app_json)
  const agentTaskTypeRef = useRef<string>('frs')

  // Attached-source lines lead the description so the build resolves the
  // EXACT source instead of inferring one from the prose (same contract as
  // the Ask-AI composer).
  const withSources = useCallback((text: string) => (
    sources.length
      ? `${sources.map((s) => s.promptLine).join('\n')}\n${text}`.trim()
      : text
  ), [sources])

  // Sources that persist INTO the project (a connection record, a registered
  // reference source) need the draft project to exist first — pre-FRS it
  // doesn't, so make it resolvable before opening the registration modal.
  const openSourceModal = useCallback(async (kind: SourceKind) => {
    if (kind === 'database' || kind === 'git' || kind === 'zip') {
      setPreparingSource(true)
      try {
        await prepareDraft()
      } catch (err) {
        toast.error(`Could not prepare the draft project: ${err instanceof Error ? err.message : err}`)
        return
      } finally {
        setPreparingSource(false)
      }
    }
    setSourceModalKind(kind)
  }, [])

  // Claude Code agent hook
  const agent = useClaudeAgent({
    project: 'Draft Application',
    onComplete: async (changedFiles) => {
      if (agentTaskTypeRef.current === 'app_json') {
        const appFile = changedFiles.find((f) => f.path.includes('application.json'))
        if (appFile) {
          toast.success('Application generated by Claude Agent!')
          setGeneratingApp(false)
          try {
            const appData = await getJson('Draft Application')
            const appId = appData?.application?.id
            if (appId && appId !== 'Draft Application') {
              await saveDraft(appId)
              await fetchProjects()
              selectProject(appId)
              navigate(`/generateapp/preview?project=${encodeURIComponent(appId)}`)
            } else {
              await fetchProjects()
              navigate(`/generateapp/preview?project=${encodeURIComponent('Draft Application')}`)
            }
          } catch {
            navigate(`/generateapp/preview?project=${encodeURIComponent('Draft Application')}`)
          }
        } else {
          toast.error('Application generation completed but no file was created')
          setGeneratingApp(false)
        }
        return
      }
      // FRS task
      const frsFile = changedFiles.find((f) => f.path.includes('requirement_doc'))
      if (frsFile) {
        toast.success('Specification created by Claude Agent!')
        onFrsGenerated(frsFile.content || '')
      }
    },
  })

  const handleBuildTogether = useCallback(() => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please provide a description of at least 10 characters')
      return
    }
    setRightPanelOpen(true)
  }, [description])

  // Actually generate the FRS (both modes), optionally carrying the confirmed
  // scope decisions from the pre-FRS question round.
  const startFrsGeneration = useCallback(async (scopeDecisions?: ScopeDecisions) => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please provide a description of at least 10 characters')
      return
    }

    const wireAttachments = att.attachments.map(toWire)
    const outgoing = withSources(description.trim())

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = 'frs'
      setAgentPanelOpen(true)
      setScopeProposal(null)
      agent.startTask('frs', {
        description: outgoing,
        suggestEnhancements,
        enhanceScope,
        selfReview,
        reviewCount,
        effort: effortConfig.frs,
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
        ...(scopeDecisions ? { scopeDecisions } : {}),
      })
      att.clear()
      setSources([])
      return
    }

    // LLM API mode (existing behavior)
    setGeneratingFrs(true)
    setProgress(0)

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval)
          return 90
        }
        return prev + Math.random() * 8
      })
    }, 1500)

    try {
      await generateFrsEnhanced({
        description: outgoing,
        suggestEnhancements,
        enhanceScope,
        selfReview,
        reviewCount,
        referencedProjects: selectedRefs.length > 0 ? selectedRefs : undefined,
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
        ...(scopeDecisions ? { scopeDecisions } : {}),
      })
      att.clear()
      setSources([])
      setProgress(100)
      clearInterval(progressInterval)
      toast.success('Specification created!')
      setScopeProposal(null)
      // Fetch the generated FRS content to show inline
      try {
        const data = await getFrsContent('Draft Application')
        onFrsGenerated(data.content || '')
      } catch {
        // Fallback: navigate to finetune if we can't load content
        navigate('/generateapp/finetune')
      }
    } catch (err: unknown) {
      clearInterval(progressInterval)
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      const msg = axiosErr?.response?.data?.detail || (err instanceof Error ? err.message : 'Failed to generate specification')
      toast.error(msg)
    } finally {
      setGeneratingFrs(false)
      setProgress(0)
    }
  }, [description, suggestEnhancements, enhanceScope, selfReview, reviewCount, navigate, generationMode, agent, onFrsGenerated, att, selectedRefs, effortConfig, withSources])

  // "Create Specification" first runs the scope-confirmation round: the AI
  // proposes scope + complexity as selectable questions; the confirmed
  // decisions are folded into the FRS prompt. Any proposal failure falls back
  // to generating directly so the step never blocks.
  const handleCreateSpecification = useCallback(async () => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please provide a description of at least 10 characters')
      return
    }
    setProposingScope(true)
    try {
      const wireAttachments = att.attachments.map(toWire)
      const proposal: ScopeProposal = await proposeScope({
        description: withSources(description.trim()),
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
      })
      if (proposal?.questions?.length) {
        setScopeProposal(proposal)
        setRightPanelOpen(false)
      } else {
        await startFrsGeneration()
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown; code?: string } } }
      // Payment/access errors must surface, never fall through to generation.
      // One carrying a `code` was already shown by the AiAccessModal (and the
      // user chose "Not now"), so a second message would only repeat it.
      if (axiosErr?.response?.status === 402) {
        const data = axiosErr.response?.data
        if (!data?.code) {
          toast.error(typeof data?.detail === 'string' ? data.detail : 'AI access required')
        }
      } else {
        toast('Skipping scope questions — generating the specification directly.', { icon: 'ℹ️' })
        await startFrsGeneration()
      }
    } finally {
      setProposingScope(false)
    }
  }, [description, att, startFrsGeneration, withSources])

  const handleGenerateApp = useCallback(async () => {
    if (!generatedFrs) return

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = 'app_json'
      setGeneratingApp(true)
      setAgentPanelOpen(true)
      agent.startTask('app_json', {
        description: 'Generate application JSON from FRS',
        effort: effortConfig.app_json,
      })
      return
    }

    // LLM API mode (existing behavior)
    setGeneratingApp(true)
    try {
      await generateAppFromFrs(generatedFrs, 'Draft Application')
      // Read the generated app to get its ID, then auto-save (rename Draft → app ID)
      const appData = await getJson('Draft Application')
      const appId = appData?.application?.id
      if (appId && appId !== 'Draft Application') {
        await saveDraft(appId)
        toast.success('Application generated!')
        await fetchProjects()
        selectProject(appId)
        navigate(`/generateapp/preview?project=${encodeURIComponent(appId)}`)
      } else {
        toast.success('Application generated!')
        await fetchProjects()
        navigate(`/generateapp/preview?project=${encodeURIComponent('Draft Application')}`)
      }
    } catch {
      toast.error('Failed to generate application from FRS')
    } finally {
      setGeneratingApp(false)
    }
  }, [generatedFrs, fetchProjects, selectProject, navigate, generationMode, agent])

  const handleSurpriseMe = () => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please provide a description of at least 10 characters')
      return
    }
    // "AI Agent" = the autonomous, multi-agent orchestrator → Build mode.
    // Attached-source lines travel inside the description so the build's
    // source detection binds to the exact registered connection/source.
    navigate(`/autoagent?project=Draft Application&mode=build&description=${encodeURIComponent(withSources(description.trim()))}`)
  }

  if (checkingDraft) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Main area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: generatedFrs ? 'stretch' : 'center',
        justifyContent: generatedFrs || scopeProposal ? 'flex-start' : 'center',
        padding: '2rem',
        minHeight: 0,
        overflowY: scopeProposal && !generatedFrs ? 'auto' : undefined,
      }}>
        {generatedFrs ? (
          /* FRS Preview after generation */
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
              flexShrink: 0,
            }}>
              <div>
                <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: 2 }}>
                  Draft Application — Specification
                </h2>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  Review the specification below, then fine-tune or generate the application.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/generateapp/finetune')}
                >
                  <Pencil size={16} />
                  Fine-tune FRS
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateApp}
                  disabled={generatingApp}
                >
                  {generatingApp ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Play size={16} />
                  )}
                  {generatingApp ? 'Generating...' : 'Generate Application'}
                </button>
              </div>
            </div>
            <div style={{
              flex: 1,
              overflow: 'auto',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              minHeight: 0,
            }}>
              <div
                className="markdown-content"
                style={{ padding: '1.5rem' }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{generatedFrs}</ReactMarkdown>
              </div>
            </div>
          </>
        ) : scopeProposal ? (
          /* Scope & complexity confirmation — the pre-FRS question round */
          <div style={{ width: '100%', maxWidth: 760 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div>
                <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: 2 }}>
                  Confirm what to build
                </h2>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  Adjust the proposed scope before the detailed specification is written — far quicker than fine-tuning the document afterwards.
                </p>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setScopeProposal(null)
                  setRightPanelOpen(true)
                }}
                disabled={generatingFrs}
              >
                Edit description
              </button>
            </div>
            <ScopeConfirmation
              proposal={scopeProposal}
              busy={generatingFrs}
              confirmLabel="Confirm & create specification"
              onConfirm={(decisions) => { void startFrsGeneration(decisions) }}
              onSkip={() => { void startFrsGeneration() }}
            />
            {generatingFrs && (
              <div style={{ marginTop: '0.875rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.375rem',
                }}>
                  <span>Generating specification…</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div style={{
                  height: 6,
                  background: 'var(--color-bg-secondary)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #0061D0, #009974)',
                    borderRadius: 'var(--radius-full)',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Description input */
          <div style={{ width: '100%', maxWidth: 720 }}>
            <h1 style={{
              fontSize: '2.0625rem',
              fontWeight: 700,
              marginBottom: '0.5rem',
              background: 'linear-gradient(90deg, #0061D0, #009974)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Describe Your Application
            </h1>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem', fontSize: '1rem' }}>
              Tell us what you want to build. Be as detailed as possible about features, users, and workflows.
            </p>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              {...att.inputHandlers}
              placeholder="Describe the application you want to build... e.g., I want a project management tool with task boards, team collaboration, file sharing, and Gantt chart views. Users should be able to create projects, assign tasks, set deadlines, and track progress."
              style={{
                width: '100%',
                minHeight: 220,
                padding: '1rem',
                fontSize: '1rem',
                lineHeight: 1.7,
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--color-text)',
                resize: 'vertical',
                fontFamily: 'var(--font-primary)',
              }}
              disabled={generatingFrs}
            />

            <div style={{ marginTop: '0.625rem' }}>
              <AttachmentChips
                attachments={att.attachments}
                onRemove={att.remove}
                disabled={generatingFrs}
              />
              {sources.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.375rem' }}>
                  {sources.map((s, i) => (
                    <span
                      key={`${s.kind}-${i}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.1875rem 0.5rem', fontSize: '0.875rem',
                        background: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '999px', color: 'var(--color-text)',
                        maxWidth: '100%',
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: 15 }}>
                        {SOURCE_TYPES.find((t) => t.kind === s.kind)?.icon || 'link'}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSources((prev) => prev.filter((_, j) => j !== i))}
                        disabled={generatingFrs}
                        title="Remove source"
                        style={{
                          display: 'inline-flex', alignItems: 'center', border: 'none',
                          background: 'transparent', cursor: 'pointer', padding: 0,
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                ref={att.fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={att.onFileInputChange}
                style={{ display: 'none' }}
              />
              {/* One "+" control — the SAME add menu as the Ask-AI dock (attach
                  a file OR add a data source), instead of separate paperclip +
                  "Add a source" buttons that overlapped. */}
              <AddSourceButton
                onAttachFile={att.openFilePicker}
                onSelectSource={(k) => { void openSourceModal(k) }}
                disabled={generatingFrs || preparingSource}
                loading={preparingSource}
                placement="bottom"
                align="left"
                title="Attach a file, or add a data source to build on — a database connection, git/zip source or IOFlow connector"
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <ReferenceProjectPicker
                value={selectedRefs}
                onChange={setSelectedRefs}
                disabled={generatingFrs}
              />
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginTop: '1.25rem',
              flexWrap: 'wrap',
            }}>
              <button
                className="btn btn-primary"
                onClick={handleBuildTogether}
                disabled={generatingFrs || !description.trim()}
              >
                <Sparkles size={16} />
                Let's Build Together
              </button>

              <button
                className="btn btn-secondary"
                onClick={handleSurpriseMe}
                disabled={generatingFrs || !description.trim()}
                title="Automatically generate FRS, models, business logic, and deploy"
              >
                <Wand2 size={16} />
                AI Agent
              </button>
            </div>

            {/* Start blank — non-AI path (shared with the Builder hub's
                "Let me build myself" advanced option). */}
            <div style={{ marginTop: '2rem' }}>
              <StartBlankForm disabled={generatingFrs} />
            </div>
          </div>
        )}
      </div>

      {/* "Add a source" — registers through the same APIs (and the same
          connection editor) the Ask-AI composer uses; a database connection
          created here is persisted in the draft project and carried through
          when the draft is saved under its real name. */}
      <AddSourceModal
        open={sourceModalKind !== null}
        kind={sourceModalKind}
        project="Draft Application"
        onClose={() => setSourceModalKind(null)}
        onAttach={(s) => setSources((prev) => [...prev, s])}
        onPickSpecFile={att.openFilePicker}
      />

      {/* Agent Stream Panel (Claude Code mode) */}
      {agentPanelOpen && generationMode === 'claude_code' && (
        <div className="responsive-panel" style={{
          width: 420,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          animation: 'slideInRight 250ms ease-out',
        }}>
          <AgentStreamPanel
            messages={agent.messages}
            isStreaming={agent.isStreaming}
            wsStatus={agent.wsStatus}
            error={agent.error}
            agentProgress={agent.agentProgress}
            onCancel={() => {
              agent.cancelTask()
              if (agentTaskTypeRef.current === 'app_json') {
                setGeneratingApp(false)
              }
            }}
            onClear={agent.clearMessages}
            onClose={() => {
              setAgentPanelOpen(false)
              if (agentTaskTypeRef.current === 'app_json') {
                setGeneratingApp(false)
              }
            }}
            onPermissionResponse={agent.respondToPermission}
            title={agentTaskTypeRef.current === 'app_json' ? 'Generating Application' : 'Creating Specification'}
          />
        </div>
      )}

      {/* Right panel - Create Specification (hidden once FRS is generated) */}
      {rightPanelOpen && !agentPanelOpen && !generatedFrs && (
        <div className="responsive-panel" style={{
          width: 340,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          animation: 'slideInRight 250ms ease-out',
          overflowY: 'auto',
        }}>
          <div>
            <h3 style={{ fontSize: '1.1875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Create Specification
            </h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
              Generate a detailed FRS document that you can fine-tune before building.
            </p>
          </div>

          <div className="divider" />

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            cursor: 'pointer',
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
          }}>
            <input
              type="checkbox"
              checked={suggestEnhancements}
              onChange={(e) => setSuggestEnhancements(e.target.checked)}
              disabled={generatingFrs}
            />
            Make it comprehensive
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            cursor: 'pointer',
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
          }}>
            <input
              type="checkbox"
              checked={enhanceScope}
              onChange={(e) => setEnhanceScope(e.target.checked)}
              disabled={generatingFrs}
            />
            Enhance scope to maximum
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            cursor: 'pointer',
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
          }}>
            <input
              type="checkbox"
              checked={selfReview}
              onChange={(e) => setSelfReview(e.target.checked)}
              disabled={generatingFrs}
            />
            Self review
          </label>

          {selfReview && (
            <div style={{ paddingLeft: '1.5rem' }}>
              <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Review count</label>
              <input
                type="number"
                min={1}
                max={5}
                value={reviewCount}
                onChange={(e) => setReviewCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                style={{ width: 80 }}
                disabled={generatingFrs}
              />
            </div>
          )}

          <div className="divider" />

          <button
            className="btn btn-primary w-full"
            onClick={handleCreateSpecification}
            disabled={generatingFrs || proposingScope || !description.trim()}
          >
            {generatingFrs || proposingScope ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <FileText size={16} />
            )}
            {proposingScope ? 'Analyzing scope…' : generatingFrs ? 'Generating...' : 'Create Specification'}
          </button>

          {generatingFrs && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.375rem',
              }}>
                <span>Generating specification...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div style={{
                height: 6,
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #0061D0, #009974)',
                  borderRadius: 'var(--radius-full)',
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
